import type { FC } from "react";
import { usePostHog } from '@posthog/react';
import { useLocation, useNavigate } from "react-router-dom";

import type { TravelDnaStep as TravelDnaStepT } from "@/_constants/TravelDnaStep.ts";
import TravelDnaStep, {
  allTravelDnaSteps,
} from "@/_constants/TravelDnaStep.ts";
import useUpsertCriticalRequirements from "@/api/dna/useUpsertCriticalRequirements.ts";
import useUpsertDna from "@/api/dna/useUpsertDna.ts";
import useUserUpdate from "@/api/users/useUserUpdate.ts";
import TravelDnaManipulationForm, {
  type OnCompleteArgs,
} from "@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import Routes from "@/router/Routes.ts";
import OnboardingStep, {
  type OnboardingStep as OnboardingStepT,
} from "$/_const/OnboardingStep.ts";

const onboardingStepMap: Record<TravelDnaStepT, OnboardingStepT> = {
  [TravelDnaStep.YOUR_NAME]: OnboardingStep.DNA_YOUR_PACE,
  [TravelDnaStep.YOUR_PACE]: OnboardingStep.DNA_YOUR_STAY,
  [TravelDnaStep.YOUR_STAY]: OnboardingStep.DNA_YOUR_SANCTUARY,
  [TravelDnaStep.YOUR_SANCTUARY]: OnboardingStep.DNA_YOUR_TABLE,
  [TravelDnaStep.YOUR_TABLE]: OnboardingStep.DNA_YOUR_FOOD_PREFERENCES,
  [TravelDnaStep.YOUR_FOOD_PREFERENCES]: OnboardingStep.DNA_YOUR_WORLD,
  [TravelDnaStep.YOUR_WORLD]: OnboardingStep.DNA_YOUR_PLANS,
  [TravelDnaStep.YOUR_PLANS]: OnboardingStep.DNA_YOUR_SPENDING,
  [TravelDnaStep.YOUR_SPENDING]: OnboardingStep.DNA_YOUR_SPLURGES,
  [TravelDnaStep.YOUR_SPLURGES]: OnboardingStep.DNA_YOUR_TRIP,
  [TravelDnaStep.YOUR_TRIP]: OnboardingStep.DNA_YOUR_DEFAULTS,
  [TravelDnaStep.YOUR_DEFAULTS]: OnboardingStep.DNA_YOUR_ACCESSIBILITY,
  [TravelDnaStep.YOUR_ACCESSIBILITY]: OnboardingStep.DNA_ANYTHING_ELSE,
  [TravelDnaStep.ANYTHING_ELSE]: OnboardingStep.DNA_CONFIRMATION,

  // Resume old saved onboarding states at their nearest replacement step.
  [TravelDnaStep.YOUR_STANDARD]: OnboardingStep.DNA_YOUR_SANCTUARY,
  [TravelDnaStep.YOUR_LOCATION]: OnboardingStep.DNA_YOUR_SANCTUARY,
  [TravelDnaStep.YOUR_LIMITS]: OnboardingStep.DNA_YOUR_DEFAULTS,
};

const legacyResumeStepMap: Partial<
  Record<TravelDnaStepT, (typeof allTravelDnaSteps)[number]>
> = {
  [TravelDnaStep.YOUR_STANDARD]: TravelDnaStep.YOUR_SANCTUARY,
  [TravelDnaStep.YOUR_LOCATION]: TravelDnaStep.YOUR_SANCTUARY,
  [TravelDnaStep.YOUR_LIMITS]: TravelDnaStep.YOUR_DEFAULTS,
};

type ActiveTravelDnaStep = (typeof allTravelDnaSteps)[number];

type DnaEditNavigationState = {
  dnaEdit?: {
    startStep: ActiveTravelDnaStep;
    endStep: ActiveTravelDnaStep;
    returnTo: string;
  };
};

const TravelDnaPage: FC = () => {
  const posthog = usePostHog();
  const { user, isOnboarding } = useAuthContext();
  const { mutateAsync: updateUser } = useUserUpdate();
  const { mutateAsync: upsertDna } = useUpsertDna();
  const { mutateAsync: upsertCriticalRequirements } =
    useUpsertCriticalRequirements();

  const navigate = useNavigate();
  const location = useLocation();

  const navigationState = location.state as DnaEditNavigationState | null;

  const requestedEdit = navigationState?.dnaEdit;

  const dnaEdit = requestedEdit &&
      allTravelDnaSteps.includes(requestedEdit.startStep) &&
      allTravelDnaSteps.includes(requestedEdit.endStep)
    ? requestedEdit
    : undefined;

  const savedStep = user?.onboarding_step?.startsWith("dna_")
    ? (user.onboarding_step.substring(4) as TravelDnaStepT)
    : undefined;

  const currentStep = savedStep
    ? allTravelDnaSteps.includes(savedStep)
      ? savedStep
      : legacyResumeStepMap[savedStep] ?? allTravelDnaSteps[0]
    : allTravelDnaSteps[0];

  const handleCompleteWhenDirty = async (
    args: OnCompleteArgs,
  ): Promise<void> => {
    try {
      if (!user || args.skipped) return;

      console.log("[DNA SAVE: submitted]", {
        step: args.step,
        data: args.data,
        context: args.context,
      });

      if (
        args.step === TravelDnaStep.YOUR_FOOD_PREFERENCES ||
        args.step === TravelDnaStep.YOUR_ACCESSIBILITY ||
        args.step === TravelDnaStep.ANYTHING_ELSE
      ) {
        const savedDna = await upsertCriticalRequirements({
          p_user_id: user.id,
          p_patch: args.data,
          p_consent: args.consent,
        });

        console.log("[DNA SAVE: critical response]", savedDna);
        return;
      }

      const savedDna = await upsertDna({
        p_user_id: user.id,
        p_patch: args.data,
      });

      console.log("[DNA SAVE: DNA response]", savedDna);
    } catch (error) {
      console.error("[DNA SAVE: failed]", error);

      throw new Error(
        (error as Error)?.message ?? "Error updating DNA",
      );
    }
  };

  const handleComplete = async (
    args: OnCompleteArgs,
  ): Promise<void> => {
    try {
      if (!user) return;

      if (dnaEdit) {
        // Editing from confirmation should always persist the submitted
        // answer, including changes from an empty initial value.
        await handleCompleteWhenDirty(args);

        const hasFinishedEditedQuestion = args.step === dnaEdit.endStep;

        const hasReachedFinalStep = args.step === TravelDnaStep.ANYTHING_ELSE;

        if (hasFinishedEditedQuestion || hasReachedFinalStep) {
          navigate(dnaEdit.returnTo, {
            replace: true,
            state: null,
          });
        }

        return;
      }

      if (!isOnboarding) return;

      await updateUser([
        { onboarding_step: onboardingStepMap[args.step] },
        { id: user.id },
      ]);

      if (args.step === TravelDnaStep.ANYTHING_ELSE) {
        posthog?.capture('travel_dna_completed');
        posthog?.capture('onboarding_step_completed', {
          onboarding_step: 'travel_dna',
        });
        navigate(Routes.ONBOARDING__DNA_CONFIRMATION, {
          replace: true,
        });
      }
    } catch (error) {
      console.error(
        "Error updating the onboarding step after a Travel DNA answer:",
        error,
      );
      posthog?.capture('onboarding_step_failed', {
        onboarding_step: args.step,
      });
      posthog?.captureException(error, {
        operation: 'save_travel_dna',
        onboarding_step: args.step,
      });

      throw new Error(
        (error as Error)?.message ?? "Error updating DNA",
      );
    }
  };

  return (
    <TravelDnaManipulationForm
      onComplete={handleComplete}
      onCompleteWhenDirty={dnaEdit ? undefined : handleCompleteWhenDirty}
      initialData={{
        ...user?.dna?.data,
        critical_requirements: user?.dna?.critical_requirements ?? {},
      }}
      initialStep={dnaEdit?.startStep ?? currentStep}
    />
  );
};

export default TravelDnaPage;
